import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, Leaf, LogOut, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABELS = { nuova: "Nuova", contattato: "Contattato", visita_fissata: "Visita fissata", chiusa: "Chiusa" };
const STATUS_COLORS = {
  nuova: "bg-terracotta/15 text-terracotta",
  contattato: "bg-sage-light text-sage",
  visita_fissata: "bg-sage text-ivory",
  chiusa: "bg-sand text-moss",
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("odl_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("tutte");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("richieste");
  const [settings, setSettings] = useState({ phone: "", mobile: "", whatsapp: "", email: "", address: "" });

  const load = useCallback(
    async (t) => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/admin/submissions`, { headers: { Authorization: `Bearer ${t}` } });
        if (res.status === 401) {
          localStorage.removeItem("odl_token");
          setToken("");
          return;
        }
        setSubs(await res.json());
      } catch {
        toast.error("Errore nel caricamento delle richieste");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Accesso non riuscito");
      localStorage.setItem("odl_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(err.message);
    }
  };

  const setStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/admin/submissions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API}/settings`);
      if (res.ok) setSettings(await res.json());
    } catch {
      toast.error("Errore nel caricamento delle impostazioni");
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/admin/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error();
      toast.success("Impostazioni salvate. Il sito è aggiornato.");
    } catch {
      toast.error("Salvataggio non riuscito");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory px-5" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm rounded-[2rem] border border-sand bg-card p-8 shadow-sm" data-testid="admin-login-form">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sage text-ivory">
            <Leaf size={20} />
          </span>
          <h1 className="mt-5 text-center font-serif text-2xl font-medium text-ink">Area Riservata</h1>
          <p className="mt-1 text-center text-xs text-moss">Orto della Longevità — gestione richieste</p>
          <input
            type="email"
            required
            data-testid="admin-email-input"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-6 w-full rounded-xl border border-sand bg-ivory px-4 py-3 text-sm outline-none focus:border-sage"
          />
          <input
            type="password"
            required
            data-testid="admin-password-input"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-3 w-full rounded-xl border border-sand bg-ivory px-4 py-3 text-sm outline-none focus:border-sage"
          />
          {error && <p className="mt-3 text-center text-xs font-semibold text-red-700" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-submit" className="mt-5 w-full rounded-full bg-sage py-3 text-sm font-semibold text-ivory transition-colors hover:bg-sage-dark">
            Accedi
          </button>
          <Link to="/" data-testid="admin-back-home" className="mt-4 block text-center text-xs text-moss hover:text-sage">
            ← Torna al sito
          </Link>
        </form>
      </div>
    );
  }

  const visible = filter === "tutte" ? subs : subs.filter((s) => s.type === filter);

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="border-b border-sand bg-ivory/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sage text-ivory">
              <Leaf size={16} />
            </span>
            <div>
              <p className="font-serif text-lg font-semibold text-ink leading-none">Area Riservata</p>
              <p className="text-[11px] text-moss">Richieste dal sito</p>
            </div>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={() => { localStorage.removeItem("odl_token"); setToken(""); }}
            className="flex items-center gap-2 rounded-full border border-sand px-4 py-2 text-xs font-semibold text-moss transition-colors hover:border-terracotta hover:text-terracotta"
          >
            <LogOut size={14} /> Esci
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="flex gap-2.5" data-testid="admin-view-tabs">
          {[["richieste", "Richieste"], ["impostazioni", "Impostazioni"]].map(([v, label]) => (
            <button
              key={v}
              data-testid={`admin-view-${v}`}
              onClick={() => { setView(v); if (v === "impostazioni") loadSettings(); }}
              className={`rounded-full px-5 py-2.5 text-xs font-semibold transition-colors ${view === v ? "bg-ink text-ivory" : "bg-card text-moss hover:bg-sand"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {view === "impostazioni" && (
          <form data-testid="settings-form" onSubmit={saveSettings} className="mt-8 max-w-xl space-y-4 rounded-3xl border border-sand bg-card p-7">
            <p className="text-sm leading-relaxed text-moss">Questi dati compaiono in tutto il sito: sezione contatti, footer, mappa e pulsanti "Chiama Ora" e WhatsApp.</p>
            {[["phone", "Telefono fisso"], ["mobile", "Cellulare"], ["whatsapp", "Numero WhatsApp (con prefisso, es. 393451234567)"], ["email", "Email"], ["address", "Indirizzo"]].map(([k, label]) => (
              <div key={k}>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-taupe">{label}</label>
                <input
                  data-testid={`settings-${k}-input`}
                  value={settings[k]}
                  onChange={(e) => setSettings({ ...settings, [k]: e.target.value })}
                  className="w-full rounded-xl border border-sand bg-ivory px-4 py-3 text-sm text-ink outline-none focus:border-sage"
                />
              </div>
            ))}
            <button type="submit" data-testid="settings-save-button" className="rounded-full bg-sage px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-sage-dark">
              Salva modifiche
            </button>
          </form>
        )}

        {view === "richieste" && (
          <>
        <div className="mt-6 flex flex-wrap gap-2.5" data-testid="admin-filters">
          {[["tutte", "Tutte"], ["contatto", "Richieste info"], ["visita", "Visite prenotate"]].map(([v, label]) => (
            <button
              key={v}
              data-testid={`admin-filter-${v}`}
              onClick={() => setFilter(v)}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${filter === v ? "bg-sage text-ivory" : "bg-card text-moss hover:bg-sage-light"}`}
            >
              {label} ({v === "tutte" ? subs.length : subs.filter((s) => s.type === v).length})
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-10 text-sm text-moss" data-testid="admin-loading">Caricamento…</p>
        ) : visible.length === 0 ? (
          <p className="mt-10 rounded-3xl border border-dashed border-sand p-10 text-center text-sm text-moss" data-testid="admin-empty">
            Nessuna richiesta presente.
          </p>
        ) : (
          <div className="mt-8 space-y-4" data-testid="admin-submissions-list">
            {visible.map((s) => (
              <article key={s.id} data-testid={`submission-card-${s.id}`} className="rounded-3xl border border-sand bg-card p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2.5">
                      <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${s.type === "visita" ? "bg-sage-light text-sage" : "bg-terracotta/10 text-terracotta"}`}>
                        {s.type === "visita" ? <CalendarCheck size={12} /> : <Mail size={12} />}
                        {s.type === "visita" ? "Visita" : "Info"}
                      </span>
                      <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${STATUS_COLORS[s.status] || STATUS_COLORS.nuova}`} data-testid={`submission-status-${s.id}`}>
                        {STATUS_LABELS[s.status] || s.status}
                      </span>
                    </div>
                    <h3 className="mt-3 font-serif text-xl font-medium text-ink">{s.name}</h3>
                    <p className="mt-1 text-xs text-moss">
                      {s.email} · {s.phone || "—"} · {new Date(s.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                    {s.type === "visita" && (
                      <p className="mt-2 text-sm text-moss">
                        Struttura: <strong className="text-ink">{s.structure}</strong>
                        {s.preferred_date && <> · Data: <strong className="text-ink">{s.preferred_date}</strong></>}
                        {s.preferred_time && <> · {s.preferred_time}</>}
                      </p>
                    )}
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-moss">{s.message || s.notes || "—"}</p>
                  </div>
                  <select
                    data-testid={`submission-status-select-${s.id}`}
                    value={s.status}
                    onChange={(e) => setStatus(s.id, e.target.value)}
                    className="rounded-xl border border-sand bg-ivory px-3 py-2 text-xs font-semibold text-ink outline-none focus:border-sage"
                  >
                    {Object.entries(STATUS_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
              </article>
            ))}
          </div>
        )}
          </>
        )}
      </main>
    </div>
  );
};

export default Admin;
